import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { isVideoFile, readVideoDurationMs } from "@/lib/parseVideoEmbed";

const MAX_FILES = 10;

/** Mirrors API team-file-rules PREVIEWABLE_MIME_PREFIXES */
const PREVIEWABLE_MIME_PREFIXES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/msword",
];

/**
 * Upload post attachment files after the post exists.
 * Returns successfully uploaded attachment payloads (best-effort; throws on first hard failure).
 */
export async function uploadPostAttachments({
  postId,
  orgId,
  files,
  accessToken,
}) {
  const list = Array.isArray(files) ? files.filter(Boolean).slice(0, MAX_FILES) : [];
  if (!list.length || !postId || !orgId || !accessToken) return [];

  const uploaded = [];
  for (const file of list) {
    const form = new FormData();
    form.append("file", file);
    if (isVideoFile(file)) {
      try {
        const durationMs = await readVideoDurationMs(file);
        form.append("duration_ms", String(durationMs));
      } catch {
        // Let the server reject if duration is required and unavailable.
      }
    }
    const { data } = await axios.post(
      `${API_BASE_URL}/posts/${postId}/attachments`,
      form,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
      }
    );
    uploaded.push(data);
  }
  return uploaded;
}

/**
 * Copy Team File blobs into File objects so they can be uploaded as post
 * attachments (posts own their own blobs; they do not store Team File links).
 */
export async function teamFileRefsToFiles({ accessToken, orgId, refs }) {
  const list = Array.isArray(refs) ? refs.filter(Boolean) : [];
  if (!list.length || !accessToken || !orgId) return [];
  const files = [];
  for (const ref of list) {
    const teamId = ref.team_id ?? ref.teamId;
    const nodeId = ref.node_id ?? ref.nodeId;
    if (teamId == null || nodeId == null) continue;
    const { data } = await axios.get(
      `${API_BASE_URL}/team/${teamId}/files/${nodeId}/content`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
        responseType: "blob",
      }
    );
    const type =
      ref.mime_type ||
      ref.mimeType ||
      data.type ||
      "application/octet-stream";
    files.push(new File([data], ref.name || "file", { type }));
  }
  return files;
}

/** Upload the single allowed image onto a workplace post comment. */
export async function uploadCommentAttachment({
  postId,
  interactionId,
  orgId,
  file,
  accessToken,
}) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(
    `${API_BASE_URL}/posts/${postId}/comments/${interactionId}/attachments`,
    form,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: { org_id: orgId },
    }
  );
  return data;
}

/**
 * Fetch an authenticated post attachment (image/video) as a blob object URL,
 * for use as MediaCarousel's `resolveSrc`.
 */
export async function resolvePostAttachmentSrc({
  postId,
  attachmentId,
  orgId,
  accessToken,
}) {
  const res = await axios.get(
    postAttachmentContentUrl(postId, attachmentId, orgId, "inline"),
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      responseType: "blob",
    }
  );
  return URL.createObjectURL(res.data);
}

export function postAttachmentContentUrl(
  postId,
  attachmentId,
  orgId,
  disposition = "attachment"
) {
  const q = new URLSearchParams({
    org_id: String(orgId),
    disposition: disposition === "inline" ? "inline" : "attachment",
  });
  return `${API_BASE_URL}/posts/${postId}/attachments/${attachmentId}/content?${q}`;
}

/** @deprecated Prefer postAttachmentContentUrl */
export function postAttachmentDownloadUrl(postId, attachmentId, orgId) {
  return postAttachmentContentUrl(postId, attachmentId, orgId, "attachment");
}

export async function deletePostAttachment({
  postId,
  attachmentId,
  orgId,
  accessToken,
}) {
  await axios.delete(
    `${API_BASE_URL}/posts/${postId}/attachments/${attachmentId}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: { org_id: orgId },
    }
  );
}

export function normalizeAttachmentMime(attachment) {
  return String(attachment?.mime_type || attachment?.mimeType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
}

export function isPostAttachmentPreviewable(attachment) {
  if (attachment?.previewable === true) return true;
  if (attachment?.previewable === false) return false;
  const mime = normalizeAttachmentMime(attachment);
  if (!mime) {
    const name = String(
      attachment?.file_name || attachment?.fileName || ""
    ).toLowerCase();
    return /\.(png|jpe?g|webp|gif|pdf|txt|md|csv|docx?|xlsx?)$/i.test(name);
  }
  return PREVIEWABLE_MIME_PREFIXES.some(
    (p) => mime === p || mime.startsWith(`${p};`)
  );
}

export { MAX_FILES as POST_ATTACHMENT_MAX_COUNT };
